<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  Pause,
  Play,
  Trash2,
  RotateCcw,
  Copy,
  FolderOpen,
  ArrowUpToLine,
  MoreVertical,
  GripVertical,
} from 'lucide-vue-next'
import TaskStatusBadge from './TaskStatusBadge.vue'
import TaskProgressBar from './TaskProgressBar.vue'
import Tooltip from '@/components/ui/Tooltip.vue'
import Dropdown from '@/components/ui/Dropdown.vue'
import ContextMenu from '@/components/ui/ContextMenu.vue'
import type { ContextMenuItem } from '@/components/ui/ContextMenu.vue'
import {
  formatBytes,
  formatSpeed,
  formatEta,
  formatPercent,
} from '@/composables/useFormat'
import { copyToClipboard } from '@/composables/useClipboard'
import { useTaskActions } from '@/composables/useTaskActions'
import { useToast } from '@/composables/useToast'
import type { TaskInfo } from '@/api/tasks'

interface Props {
  task: TaskInfo
  selected?: boolean
  draggable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  selected: false,
  draggable: false,
})

const emit = defineEmits<{
  click: [gid: string]
  'context-menu': [gid: string, event: MouseEvent]
  dragstart: [gid: string, event: DragEvent]
  dragover: [gid: string, event: DragEvent]
  dragend: [gid: string, event: DragEvent]
  drop: [gid: string, event: DragEvent]
}>()

const { t } = useI18n()
const toast = useToast()
const actions = useTaskActions()
const contextRef = ref<InstanceType<typeof ContextMenu> | null>(null)

const progressValue = computed(() => props.task.progress ?? 0)
const isError = computed(() => props.task.status === 'error')
const isActive = computed(
  () => props.task.status === 'active' || props.task.status === 'downloading',
)
const isPaused = computed(() => props.task.status === 'paused')
const isWaiting = computed(
  () => props.task.status === 'waiting' || props.task.status === 'pending',
)
const isComplete = computed(() => props.task.status === 'complete')

const progressVariant = computed<'primary' | 'success' | 'warning' | 'danger'>(() => {
  if (isError.value) return 'danger'
  if (isComplete.value) return 'success'
  if (isPaused.value) return 'warning'
  return 'primary'
})

const fileCount = computed(() => props.task.files?.length ?? 1)

const dotClass = computed(() => {
  if (isError.value) return 'bg-danger'
  if (isActive.value) return 'bg-success animate-pulse'
  if (isPaused.value) return 'bg-warning'
  if (isWaiting.value) return 'bg-info'
  if (isComplete.value) return 'bg-fg-subtle'
  return 'bg-fg-subtle'
})

function onActionClick(e: MouseEvent, fn: () => void): void {
  e.stopPropagation()
  fn()
}

function onMoreClick(e: MouseEvent): void {
  e.stopPropagation()
}

function buildMenuItems(): ContextMenuItem[] {
  return [
    {
      key: 'pause',
      label: t('tasks.pause'),
      icon: Pause,
      disabled: !isActive.value,
    },
    {
      key: 'resume',
      label: t('tasks.resume'),
      icon: Play,
      disabled: !isPaused.value,
    },
    {
      key: 'retry',
      label: t('tasks.retry'),
      icon: RotateCcw,
      disabled: !isError.value && !isComplete.value,
    },
    {
      key: 'prioritize',
      label: t('tasks.prioritize'),
      icon: ArrowUpToLine,
      disabled: !isWaiting.value,
    },
    {
      key: 'copyGid',
      label: t('tasks.copyGid'),
      icon: Copy,
    },
    {
      key: 'reveal',
      label: t('tasks.revealFiles'),
      icon: FolderOpen,
    },
    {
      key: 'remove',
      label: t('tasks.remove'),
      icon: Trash2,
      danger: true,
      divided: true,
    },
  ]
}

async function onContextSelect(key: string): Promise<void> {
  switch (key) {
    case 'pause':
      await actions.pause(props.task)
      break
    case 'resume':
      await actions.resume(props.task)
      break
    case 'retry':
      await actions.retry(props.task)
      break
    case 'prioritize':
      await actions.move(props.task, 0, 'POS_SET')
      break
    case 'copyGid': {
      const ok = await copyToClipboard(props.task.gid)
      if (ok) toast.success(t('tasks.toast.copiedGid'))
      else toast.error(t('tasks.toast.copyFailed'))
      break
    }
    case 'reveal':
      toast.info(t('tasks.toast.revealHint'))
      break
    case 'remove':
      await actions.remove(props.task)
      break
  }
}

function onCardClick(): void {
  emit('click', props.task.gid)
}

function onContext(e: MouseEvent): void {
  emit('context-menu', props.task.gid, e)
}

function onDragStart(e: DragEvent): void {
  if (!props.draggable) return
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', props.task.gid)
  }
  emit('dragstart', props.task.gid, e)
}

function onDragOver(e: DragEvent): void {
  if (!props.draggable) return
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
  emit('dragover', props.task.gid, e)
}

function onDragEnd(e: DragEvent): void {
  if (!props.draggable) return
  emit('dragend', props.task.gid, e)
}

function onDrop(e: DragEvent): void {
  if (!props.draggable) return
  emit('drop', props.task.gid, e)
}
</script>

<template>
  <div
    v-memo="[task.gid, task.status, task.progress, task.speed, task.uploadSpeedNum, task.eta, task.connectionsNum, task.error, task.total, task.done, selected, draggable]"
    class="group relative flex items-stretch gap-3 rounded-md border border-border bg-bg-surface px-3 py-2.5 transition-colors duration-fast hover:border-border-strong hover:bg-bg-hover/40 cursor-pointer"
    :class="[
      selected ? 'ring-2 ring-brand-500 border-brand-500/50' : '',
      isError ? 'border-danger/40' : '',
    ]"
    role="button"
    tabindex="0"
    :draggable="draggable"
    @click="onCardClick"
    @contextmenu.prevent="onContext"
    @keydown.enter="onCardClick"
    @keydown.space.prevent="onCardClick"
    @dragstart="onDragStart"
    @dragover.prevent="onDragOver"
    @drop.prevent="onDrop"
    @dragend="onDragEnd"
  >
    <!-- Drag handle (only when draggable) -->
    <div
      v-if="draggable"
      class="flex items-center text-fg-subtle opacity-0 group-hover:opacity-100 transition-opacity"
      :aria-label="t('tasks.dragHandle')"
    >
      <GripVertical :size="14" />
    </div>

    <!-- Status dot -->
    <div class="flex items-center">
      <span class="relative inline-flex h-2.5 w-2.5">
        <span class="absolute inset-0 rounded-full" :class="dotClass" />
      </span>
    </div>

    <!-- Main info -->
    <div class="min-w-0 flex-1">
      <div class="flex items-center gap-2">
        <span
          class="truncate text-sm font-medium text-fg"
          :title="task.name ?? task.gid"
        >
          {{ task.name ?? task.gid }}
        </span>
        <TaskStatusBadge :status="task.status" />
        <!-- v3.13.58: 镜像任务 — 其它节点代为执行, 显示执行节点 -->
        <span
          v-if="task.targetNode"
          class="shrink-0 rounded bg-brand-500/15 px-1.5 py-0.5 text-xs font-medium text-brand-600 dark:text-brand-300"
          :title="t('tasks.executingOn', { node: task.targetNode })"
        >
          {{ t('tasks.executingOn', { node: task.targetNode }) }}
        </span>
        <!-- v3.19.37: 任务分类徽章 -->
        <span
          v-if="task.category"
          class="shrink-0 rounded bg-bg-elevated px-1.5 py-0.5 text-xs font-medium text-fg-muted"
          :title="t('tasks.category')"
        >
          {{ task.category }}
        </span>
        <span
          v-if="fileCount > 1"
          class="rounded bg-bg-elevated px-1.5 py-0.5 text-xs font-medium text-fg-muted"
        >
          {{ fileCount }} {{ t('tasks.files') }}
        </span>
      </div>

      <div class="mt-1.5">
        <TaskProgressBar :value="progressValue" :variant="progressVariant" size="sm" />
      </div>

      <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-fg-muted">
        <span class="font-mono">{{ formatPercent(progressValue) }}</span>
        <span>
          {{ formatBytes(task.done ?? 0) }} / {{ formatBytes(task.total ?? 0) }}
        </span>
        <span v-if="(task.speed ?? 0) > 0" class="text-fg">
          ↓ {{ formatSpeed(task.speed ?? 0) }}
        </span>
        <span v-if="(task.uploadSpeedNum ?? 0) > 0" class="text-fg-muted">
          ↑ {{ formatSpeed(task.uploadSpeedNum ?? 0) }}
        </span>
        <span v-if="(task.eta ?? -1) >= 0">
          {{ t('tasks.fields.eta') }}: {{ formatEta(task.eta ?? -1) }}
        </span>
        <span v-if="(task.connectionsNum ?? 0) > 0">
          {{ task.connectionsNum }}<template v-if="(task.connectionsMaxNum ?? 0) > 0">/{{ task.connectionsMax }}</template>
          {{ t('tasks.connections') }}
        </span>
        <span v-if="task.error" class="text-danger truncate max-w-[20rem]" :title="task.error">
          {{ task.error }}
        </span>
        <span class="font-mono text-fg-subtle truncate max-w-[14rem]" :title="task.gid">
          {{ task.gid }}
        </span>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex shrink-0 items-center gap-1" @click.stop>
      <Tooltip :content="isPaused ? t('tasks.resume') : t('tasks.pause')">
        <button
          v-if="isActive || isPaused"
          type="button"
          class="rounded p-1.5 text-fg-muted hover:bg-bg-hover hover:text-fg"
          :aria-label="isPaused ? t('tasks.resume') : t('tasks.pause')"
          @click="onActionClick($event, () => isPaused ? actions.resume(task) : actions.pause(task))"
        >
          <Pause v-if="isActive" :size="14" />
          <Play v-else :size="14" />
        </button>
      </Tooltip>

      <Tooltip v-if="isWaiting" :content="t('tasks.prioritize')">
        <button
          type="button"
          class="rounded p-1.5 text-fg-muted hover:bg-bg-hover hover:text-fg"
          :aria-label="t('tasks.prioritize')"
          @click="onActionClick($event, () => actions.move(task, 0, 'POS_SET'))"
        >
          <ArrowUpToLine :size="14" />
        </button>
      </Tooltip>

      <Tooltip v-if="isError || isComplete" :content="t('tasks.retry')">
        <button
          type="button"
          class="rounded p-1.5 text-fg-muted hover:bg-bg-hover hover:text-fg"
          :aria-label="t('tasks.retry')"
          @click="onActionClick($event, () => actions.retry(task))"
        >
          <RotateCcw :size="14" />
        </button>
      </Tooltip>

      <Dropdown
        :items="buildMenuItems()"
        placement="bottom-end"
        @select="onContextSelect"
      >
        <button
          type="button"
          class="rounded p-1.5 text-fg-muted hover:bg-bg-hover hover:text-fg"
          :aria-label="t('common.actions')"
          @click="onMoreClick"
        >
          <MoreVertical :size="14" />
        </button>
      </Dropdown>

      <Tooltip :content="t('tasks.remove')">
        <button
          type="button"
          class="rounded p-1.5 text-fg-muted hover:bg-danger/10 hover:text-danger"
          :aria-label="t('tasks.remove')"
          @click="onActionClick($event, () => actions.remove(task))"
        >
          <Trash2 :size="14" />
        </button>
      </Tooltip>
    </div>

    <ContextMenu ref="contextRef" :items="[]" @select="onContextSelect" />
  </div>
</template>